import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, BarChart, PieChart, Pie, Cell,
} from "recharts";
import {
  X, Download, Building2, Users, Truck, ShieldCheck, HardHat, Wallet,
  TrendingUp, AlertTriangle, Gauge, ListChecks, Home, FileText,
} from "lucide-react";

const TEAL = "#2f7d5b", GOLD = "#c9a961", GREEN = "#10b981", RED = "#ef4444";
const PIE = ["#c9a961", "#2f7d5b", "#7fd1ae", "#a3823f", "#4d5a24", "#7f9b8e", "#b23a48"];

const statusChip = (s) => {
  const map = {
    paid: "bg-[#10b981]/15 text-[#7fd1ae] border-[#10b981]/30",
    due: "bg-[#c9a961]/15 text-[#e6cd91] border-[#c9a961]/30",
    overdue: "bg-[#ef4444]/15 text-[#fca5a5] border-[#ef4444]/30",
  };
  return map[s] || map.due;
};

const Section = ({ icon: Icon, title, right, children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 lg:p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2 text-[#c9a961]">
        {Icon && <Icon className="h-4 w-4" />}
        <h3 className="font-head text-lg text-white">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </div>
);

export default function ConstructionSimulation({ sim, fmt, onClose, onDownloadPdf, pdfLoading }) {
  if (!sim) return null;
  const kfmt = (k) => (k.unit === "$" ? fmt(k.value) : k.unit === "%" ? `${k.value}%` : `${k.value}`);
  const pnl = sim.pnl;

  return (
    <div data-testid="construction-sim" className="mt-10 space-y-6">
      {/* Header */}
      <div className="rounded-2xl border border-[#c9a961]/30 bg-gradient-to-br from-[#c9a961]/[0.08] to-transparent p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#c9a961] text-xs uppercase tracking-[0.2em]">
              <Building2 className="h-4 w-4" /> Live simulation · Expense AI
            </div>
            <h2 className="mt-3 font-head text-3xl lg:text-4xl text-white tracking-tight">{sim.business_name}</h2>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/70">{sim.summary}</p>
            <p className="mt-2 text-[11px] italic text-white/40">{sim.note}</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="sim-download-pdf" onClick={onDownloadPdf} disabled={pdfLoading}
              className="inline-flex items-center gap-2 rounded-full bg-[#c9a961] px-4 py-2 text-xs font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50 transition-colors">
              <Download className="h-3.5 w-3.5" /> {pdfLoading ? "Building…" : "Download full report"}
            </button>
            <button data-testid="sim-close" onClick={onClose}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-xs text-white/70 hover:border-white/40 hover:text-white transition-colors">
              <X className="h-3.5 w-3.5" /> Close
            </button>
          </div>
        </div>
        {/* KPI grid */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sim-kpis">
          {sim.kpis.map((k) => (
            <div key={k.key} data-testid={`sim-kpi-${k.key}`} className="rounded-xl border border-white/10 bg-[#0f2624]/60 px-3 py-3">
              <div className="text-[9px] uppercase tracking-wide text-white/40">{k.label}</div>
              <div className="mt-1 text-lg font-semibold text-white tabular-nums">{kfmt(k)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Revenue vs expense trend */}
      <Section icon={TrendingUp} title="24-month revenue, expenses & profit" testid="sim-trend">
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={sim.trend} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
              <XAxis dataKey="label" tick={{ fill: "#ffffff70", fontSize: 10 }} interval={1} />
              <YAxis tick={{ fill: "#ffffff70", fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: "#0f2624", border: "1px solid #ffffff22", borderRadius: 12, color: "#fff" }}
                formatter={(v, n) => [fmt(v), n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="revenue" name="Revenue" fill={GOLD} radius={[3, 3, 0, 0]} barSize={7} />
              <Bar dataKey="expenses" name="Expenses" fill={RED} radius={[3, 3, 0, 0]} barSize={7} />
              <Line type="monotone" dataKey="profit" name="Net profit" stroke={GREEN} strokeWidth={2.4} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* P&L */}
        <Section icon={FileText} title="Profit & loss (annual)" testid="sim-pnl">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-white/10">
                <td className="py-2 text-white/90 font-medium">Revenue</td>
                <td className="py-2 text-right text-white tabular-nums font-semibold">{fmt(pnl.revenue)}</td>
              </tr>
              {pnl.cogs.map((c) => (
                <tr key={c.label} className="border-b border-white/[0.06]">
                  <td className="py-1.5 pl-4 text-white/55">COGS · {c.label}</td>
                  <td className="py-1.5 text-right text-white/70 tabular-nums">({fmt(c.amount)})</td>
                </tr>
              ))}
              <tr className="border-b border-white/10">
                <td className="py-2 text-[#7fd1ae] font-medium">Gross profit</td>
                <td className="py-2 text-right text-[#7fd1ae] tabular-nums font-semibold">{fmt(pnl.gross_profit)} · {pnl.gross_margin}%</td>
              </tr>
              {pnl.opex.map((o) => (
                <tr key={o.label} className="border-b border-white/[0.06]">
                  <td className="py-1.5 pl-4 text-white/55">OpEx · {o.label}</td>
                  <td className="py-1.5 text-right text-white/70 tabular-nums">({fmt(o.amount)})</td>
                </tr>
              ))}
              <tr>
                <td className="py-2.5 text-white font-semibold">Net income</td>
                <td className="py-2.5 text-right text-[#10b981] tabular-nums font-bold text-base">{fmt(pnl.net_income)}</td>
              </tr>
            </tbody>
          </table>
        </Section>

        {/* Spend breakdown donut */}
        <Section icon={Wallet} title="Where the money goes" testid="sim-breakdown-pie">
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={sim.expense_breakdown} dataKey="amount" nameKey="label" cx="42%" cy="50%"
                  innerRadius={48} outerRadius={92} paddingAngle={2}>
                  {sim.expense_breakdown.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f2624", border: "1px solid #ffffff22", borderRadius: 12, color: "#fff" }}
                  formatter={(v, n) => [fmt(v) + "/yr", n]} />
                <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 10, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      {/* Current vs Elevare */}
      <Section icon={TrendingUp} title="Current spend vs. achievable with Elevare"
        right={<span className="text-xs text-[#7fd1ae]">Save {fmt(sim.annual_savings)}/yr</span>} testid="sim-savings-bars">
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sim.expense_breakdown} margin={{ top: 8, right: 12, left: 4, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
              <XAxis dataKey="label" tick={{ fill: "#ffffff70", fontSize: 10 }} angle={-18} textAnchor="end" interval={0} height={60} />
              <YAxis tick={{ fill: "#ffffff70", fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: "#0f2624", border: "1px solid #ffffff22", borderRadius: 12, color: "#fff" }}
                formatter={(v, n) => [fmt(v), n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="amount" name="Current" fill={RED} radius={[3, 3, 0, 0]} />
              <Bar dataKey="projected" name="With Elevare" fill={GREEN} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* Homes under construction */}
      <Section icon={Home} title="Homes under construction (5)"
        right={<span className="text-xs text-white/50">Backlog {fmt(sim.backlog)} · A/R {fmt(sim.ar_uncollected)}</span>} testid="sim-homes">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {sim.homes.map((h) => (
            <div key={h.name} data-testid={`sim-home-${h.name}`} className="rounded-xl border border-white/10 bg-[#0f2624]/60 p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white">{h.name}</div>
                <span className="text-[10px] text-white/50">{h.status}</span>
              </div>
              <div className="mt-3 h-2 w-full rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#c9a961]" style={{ width: `${h.pct_complete}%` }} />
              </div>
              <div className="mt-1 text-[10px] text-white/40">{h.pct_complete}% complete</div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                <div><span className="text-white/40">Contract</span><div className="text-white tabular-nums">{fmt(h.contract)}</div></div>
                <div><span className="text-white/40">Spent</span><div className="text-white tabular-nums">{fmt(h.spent)}</div></div>
                <div><span className="text-white/40">Billed</span><div className="text-white tabular-nums">{fmt(h.billed)}</div></div>
                <div><span className="text-white/40">Received</span><div className="text-white tabular-nums">{fmt(h.received)}</div></div>
              </div>
              {h.uncollected > 0 && (
                <div className="mt-2 text-[11px] text-[#fca5a5]">Uncollected: {fmt(h.uncollected)}</div>
              )}
            </div>
          ))}
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payroll */}
        <Section icon={Users} title={`Payroll · ${sim.payroll.headcount} employees`} testid="sim-payroll">
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="rounded-lg border border-[#10b981]/25 bg-[#10b981]/[0.06] px-3 py-2">
              <div className="text-[9px] uppercase tracking-wide text-white/40">Last run · paid</div>
              <div className="text-sm font-semibold text-[#7fd1ae] tabular-nums">{fmt(sim.payroll.payouts.paid)}</div>
              <div className="text-[9px] text-white/40">{sim.payroll.last_paid_date}</div>
            </div>
            <div className="rounded-lg border border-[#c9a961]/25 bg-[#c9a961]/[0.06] px-3 py-2">
              <div className="text-[9px] uppercase tracking-wide text-white/40">Next run · projected</div>
              <div className="text-sm font-semibold text-[#e6cd91] tabular-nums">{fmt(sim.payroll.payouts.projected)}</div>
              <div className="text-[9px] text-white/40">{sim.payroll.next_run_date}</div>
            </div>
            <div className="rounded-lg border border-[#ef4444]/25 bg-[#ef4444]/[0.06] px-3 py-2">
              <div className="text-[9px] uppercase tracking-wide text-white/40">Held · unpaid</div>
              <div className="text-sm font-semibold text-[#fca5a5] tabular-nums">{fmt(sim.payroll.payouts.unpaid)}</div>
              <div className="text-[9px] text-white/40">Timesheet review</div>
            </div>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-white/40 border-b border-white/10">
                <th className="py-1.5 text-left font-medium">Role</th>
                <th className="py-1.5 text-center font-medium">#</th>
                <th className="py-1.5 text-right font-medium">Avg / yr</th>
                <th className="py-1.5 text-right font-medium">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {sim.payroll.roles.map((r) => (
                <tr key={r.role} className="border-b border-white/[0.06]">
                  <td className="py-1.5 text-white/80">{r.role}
                    <span className={`ml-2 text-[9px] uppercase ${r.group === "field" ? "text-[#7fd1ae]" : "text-[#c9a961]"}`}>{r.group}</span>
                  </td>
                  <td className="py-1.5 text-center text-white/60">{r.count}</td>
                  <td className="py-1.5 text-right text-white/60 tabular-nums">{fmt(r.avg_annual)}</td>
                  <td className="py-1.5 text-right text-white tabular-nums">{fmt(r.subtotal)}</td>
                </tr>
              ))}
              <tr>
                <td className="py-2 text-white font-semibold" colSpan={3}>Total payroll</td>
                <td className="py-2 text-right text-white font-bold tabular-nums">{fmt(sim.payroll.total_annual)}</td>
              </tr>
            </tbody>
          </table>
        </Section>

        {/* Fleet */}
        <Section icon={Truck} title="Fleet — 6 vehicles"
          right={<span className="text-xs text-white/50">{fmt(sim.fleet_monthly)}/mo · {fmt(sim.fleet_annual)}/yr</span>} testid="sim-fleet">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-white/40 border-b border-white/10">
                <th className="py-1.5 text-left font-medium">Vehicle</th>
                <th className="py-1.5 text-right font-medium">Finance</th>
                <th className="py-1.5 text-right font-medium">Fuel</th>
                <th className="py-1.5 text-right font-medium">Maint.</th>
                <th className="py-1.5 text-right font-medium">Ins.</th>
                <th className="py-1.5 text-right font-medium">/mo</th>
              </tr>
            </thead>
            <tbody>
              {sim.vehicles.map((v) => (
                <tr key={v.name} className="border-b border-white/[0.06]">
                  <td className="py-1.5 text-white/80">{v.name}
                    <span className="ml-1.5 text-[9px] text-white/40">{v.year} · {v.finance}</span>
                  </td>
                  <td className="py-1.5 text-right text-white/60 tabular-nums">{fmt(v.monthly.finance)}</td>
                  <td className="py-1.5 text-right text-white/60 tabular-nums">{fmt(v.monthly.fuel)}</td>
                  <td className="py-1.5 text-right text-white/60 tabular-nums">{fmt(v.monthly.maintenance)}</td>
                  <td className="py-1.5 text-right text-white/60 tabular-nums">{fmt(v.monthly.insurance)}</td>
                  <td className="py-1.5 text-right text-white tabular-nums">{fmt(v.monthly.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Accounts payable */}
        <Section icon={Wallet} title="Accounts payable"
          right={<span className="text-xs text-white/50">Outstanding {fmt(sim.ap_outstanding)} · Overdue {fmt(sim.ap_overdue)}</span>} testid="sim-ap">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-white/40 border-b border-white/10">
                <th className="py-1.5 text-left font-medium">Vendor</th>
                <th className="py-1.5 text-left font-medium">Job</th>
                <th className="py-1.5 text-right font-medium">Amount</th>
                <th className="py-1.5 text-right font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {sim.accounts_payable.map((a, i) => (
                <tr key={i} className="border-b border-white/[0.06]">
                  <td className="py-1.5 text-white/80">{a.vendor}<span className="ml-1.5 text-[9px] text-white/40">{a.category}</span></td>
                  <td className="py-1.5 text-white/55">{a.job}</td>
                  <td className="py-1.5 text-right text-white tabular-nums">{fmt(a.amount)}</td>
                  <td className="py-1.5 text-right">
                    <span className={`inline-block rounded-full border px-2 py-0.5 text-[9px] uppercase ${statusChip(a.status)}`}>{a.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        {/* Insurance */}
        <Section icon={ShieldCheck} title="Insurance program (annual)" testid="sim-insurance">
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={sim.insurance} margin={{ top: 4, right: 16, left: 40, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#ffffff70", fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="label" tick={{ fill: "#ffffff85", fontSize: 10 }} width={110} />
                <Tooltip contentStyle={{ background: "#0f2624", border: "1px solid #ffffff22", borderRadius: 12, color: "#fff" }}
                  formatter={(v) => [fmt(v), "Annual premium"]} />
                <Bar dataKey="amount" fill={GOLD} radius={[0, 4, 4, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Health & BBB */}
        <Section icon={Gauge} title="Business Health Score & BBB projection" testid="sim-health">
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="rounded-lg bg-white/5 px-3 py-2"><div className="text-[9px] uppercase text-white/40">Health</div><div className="text-xl font-semibold text-white">{sim.health.health_score}</div></div>
            <div className="rounded-lg bg-white/5 px-3 py-2"><div className="text-[9px] uppercase text-white/40">Risk</div><div className="text-sm font-semibold text-white mt-1">{sim.health.risk_level}</div></div>
            <div className="rounded-lg bg-white/5 px-3 py-2"><div className="text-[9px] uppercase text-white/40">BBB</div><div className="text-xl font-semibold text-[#7fd1ae]">{sim.health.current_bbb}→{sim.health.projected_bbb}</div></div>
            <div className="rounded-lg bg-white/5 px-3 py-2"><div className="text-[9px] uppercase text-white/40">Save/yr</div><div className="text-sm font-semibold text-[#c9a961] mt-1 tabular-nums">{fmt(sim.health.projected_savings)}</div></div>
          </div>
          <div className="space-y-2">
            {sim.health.categories.map((c) => (
              <div key={c.name}>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-white/70">{c.name}</span>
                  <span className="text-white/50 tabular-nums">{c.score}</span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${c.score}%`, background: c.score >= 70 ? GREEN : c.score >= 55 ? GOLD : RED }} />
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Recommendations + risks */}
        <Section icon={ListChecks} title="Cost-cutting plan & risks" testid="sim-plan">
          <ol className="space-y-2.5">
            {sim.recommendations.map((r, i) => (
              <li key={i} className="text-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-white/90 font-medium">{i + 1}. {r.title}</span>
                  {r.est_monthly_savings > 0 && <span className="shrink-0 text-[11px] text-[#7fd1ae] tabular-nums">{fmt(r.est_monthly_savings)}/mo</span>}
                </div>
                <p className="mt-0.5 text-[12px] text-white/50 leading-snug">{r.detail}</p>
              </li>
            ))}
          </ol>
          <div className="mt-5 border-t border-white/10 pt-4">
            <div className="flex items-center gap-2 text-[#f59e0b] text-xs uppercase tracking-wide mb-2"><AlertTriangle className="h-3.5 w-3.5" /> Risks to watch</div>
            <ul className="space-y-1.5">
              {sim.risks.map((rk, i) => <li key={i} className="text-[12px] text-white/60 leading-snug">• {rk}</li>)}
            </ul>
          </div>
        </Section>
      </div>
    </div>
  );
}
